import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { VideoAPI } from '../../services/feature-api';
import { markdownToHtml } from '../../utils/markdownToHtml';

// Video đào tạo trên Mux — /video/:ma (bắt đăng nhập qua AuthWrapper). BE: PE_Core modules/video (bảng video_dao_tao, video_xem).
// Player = web component <mux-player> nạp từ CDN trong public/index.html (không thêm npm dep).
// Tiến độ: cộng dồn giây THẬT đang phát (timeupdate, mỗi nhịp ≤ 1,5s) → cứ 10s / pause / ended / rời trang báo BE 1 lần.
// metadata-viewer-user-id = mã đại lý → Mux Data (dashboard Mux) cũng thấy từng người xem.

const Icon = ({ name, size = 16, className = '' }) => (
  <span className={`material-symbols-rounded ${className}`} style={{ fontSize: size, lineHeight: 1 }}>{name}</span>
);
const fmtPhut = (s) => `${Math.floor((s || 0) / 60)}:${String(Math.floor((s || 0) % 60)).padStart(2, '0')}`;

const VideoPage = ({ accessToken, userInfo }) => {
  const { ma } = useParams();
  const navigate = useNavigate();
  const [video, setVideo] = useState(null);
  const [loi, setLoi] = useState('');
  const [tienDo, setTienDo] = useState(null);
  const playerRef = useRef(null);
  const dem = useRef({ xemThem: 0, lastT: null, timer: null }); // giây đã phát chưa báo + mốc timeupdate trước

  useEffect(() => {
    if (!accessToken || !ma) return;
    VideoAPI.xem(accessToken, ma).then((r) => {
      if (r.success && r.data) { setVideo(r.data); setTienDo(r.data.tienDo); }
      else setLoi(r.error || r.message || 'Không mở được video');
    });
  }, [accessToken, ma]);

  const bao = useCallback((keepalive = false) => {
    const p = playerRef.current;
    const d = dem.current;
    if (!p || !accessToken) return;
    const thoiLuong = Math.floor(p.duration || 0);
    if (!thoiLuong || (d.xemThem < 1 && !keepalive)) return;
    const body = { giay: Math.floor(p.currentTime || 0), thoiLuong, xemThem: Math.floor(d.xemThem) };
    d.xemThem -= body.xemThem;
    VideoAPI.tienDo(accessToken, ma, body, keepalive).then((r) => { if (r?.success && r.data) setTienDo((t) => ({ ...t, ...r.data })); });
  }, [accessToken, ma]);

  useEffect(() => {
    const p = playerRef.current;
    if (!p || !video) return;
    const d = dem.current;
    const onTime = () => {
      const t = p.currentTime || 0;
      if (d.lastT !== null && !p.paused) {
        const delta = t - d.lastT;
        if (delta > 0 && delta < 1.5) d.xemThem += delta; // tua (delta lớn) hoặc lùi (delta âm) → không cộng
      }
      d.lastT = t;
    };
    const onPlay = () => { d.lastT = p.currentTime || 0; if (!d.timer) d.timer = setInterval(() => bao(), 10000); };
    const onDung = () => { clearInterval(d.timer); d.timer = null; bao(); };
    const onRoi = () => { if (document.visibilityState === 'hidden') bao(true); };
    p.addEventListener('timeupdate', onTime);
    p.addEventListener('play', onPlay);
    p.addEventListener('pause', onDung);
    p.addEventListener('ended', onDung);
    document.addEventListener('visibilitychange', onRoi);
    window.addEventListener('pagehide', () => bao(true));
    return () => {
      p.removeEventListener('timeupdate', onTime); p.removeEventListener('play', onPlay);
      p.removeEventListener('pause', onDung); p.removeEventListener('ended', onDung);
      document.removeEventListener('visibilitychange', onRoi);
      clearInterval(d.timer); d.timer = null; bao(true);
    };
  }, [video, bao]);

  const pt = tienDo?.phan_tram || 0;
  const xong = !!tienDo?.xong_luc || pt >= 90;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="flex items-center gap-2 px-3 py-2 bg-white border-b border-gray-200 sticky top-0 z-10">
        <button type="button" onClick={() => navigate('/dashboard')} className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium text-emerald-800 bg-emerald-50">
          <Icon name="arrow_back" size={14} /><span>Trang chính</span>
        </button>
        <h1 className="text-sm font-bold text-gray-900 flex-1 truncate">{video?.ten || 'Video đào tạo'}</h1>
        {video?.toi?.code && <span className="text-[11px] text-gray-500 truncate max-w-[110px]">{video.toi.code} - {video.toi.facebook}</span>}
      </div>

      {loi && <div className="m-3 p-3 rounded-xl bg-red-50 text-red-700 text-sm">{loi}</div>}

      {video && (
        <div className="max-w-md mx-auto p-3 space-y-3">
          <div className="rounded-2xl overflow-hidden bg-black" style={{ maxHeight: '78vh' }}>
            <mux-player
              ref={playerRef}
              playback-id={video.mux_playback_id}
              stream-type="on-demand"
              metadata-video-title={video.ten}
              metadata-viewer-user-id={video.toi?.code || userInfo?.id || ''}
              accent-color="#277f4d"
              playsinline=""
              style={{ width: '100%', maxHeight: '78vh', aspectRatio: '9 / 16', display: 'block' }}
            />
          </div>

          <div className="rounded-2xl bg-white border border-gray-200 p-3">
            <div className="flex items-center justify-between text-xs mb-1.5">
              <span className="font-semibold text-gray-800">{xong ? '✅ Bạn đã xem xong' : 'Bạn đã xem'}</span>
              <span className="font-bold" style={{ color: xong ? '#16A34A' : '#277f4d' }}>{pt}%</span>
            </div>
            <div className="h-2.5 rounded-full bg-gray-100 overflow-hidden">
              <div className="h-full rounded-full transition-all" style={{ width: `${pt}%`, background: xong ? '#16A34A' : '#277f4d' }} />
            </div>
            <div className="flex justify-between text-[11px] text-gray-500 mt-1.5">
              <span>Đã xem {fmtPhut(video.thoi_luong_giay ? Math.min(tienDo?.tong_giay || 0, video.thoi_luong_giay) : tienDo?.tong_giay)}{video.thoi_luong_giay ? ` / ${fmtPhut(video.thoi_luong_giay)}` : ''}</span>
              <span>Tính là xong khi xem đủ 90%</span>
            </div>
          </div>

          {video.huong_dan && (
            <div className="rounded-2xl bg-white border border-gray-200 p-3 huong-dan text-sm"
                 dangerouslySetInnerHTML={{ __html: markdownToHtml(video.huong_dan) }} />
          )}
        </div>
      )}
    </div>
  );
};

export default VideoPage;
